<script setup>
import { computed, ref, nextTick, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { vOnClickOutside } from '@vueuse/components';

import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import EmojiPicker from './EmojiPicker.vue';

const props = defineProps({
  modelValue: {
    type: Object,
    required: true,
  },
  parameterType: {
    type: String,
    default: 'positional',
  },
});

const emit = defineEmits(['update:modelValue']);

const { t } = useI18n();

const textareaRef = ref(null);
const showEmojiPicker = ref(false);
const showExpandedEditor = ref(false);
const undoStack = ref([]);
const redoStack = ref([]);

const bodyData = computed({
  get: () => props.modelValue,
  set: value => emit('update:modelValue', value),
});

// Simple text update - don't manage examples here
// Examples are populated by TemplateBuilder.generateComponents()
const updateText = (value, addToHistory = true) => {
  // Add current state to undo stack before updating
  if (addToHistory && bodyData.value.text !== value) {
    undoStack.value.push(bodyData.value.text);
    // Limit undo stack to 50 items
    if (undoStack.value.length > 50) {
      undoStack.value.shift();
    }
    // Clear redo stack when new change is made
    redoStack.value = [];
  }

  bodyData.value = {
    ...bodyData.value,
    text: value,
  };
};

const undo = () => {
  if (undoStack.value.length === 0) return;

  const previousState = undoStack.value.pop();
  redoStack.value.push(bodyData.value.text);
  updateText(previousState, false);
};

const redo = () => {
  if (redoStack.value.length === 0) return;

  const nextState = redoStack.value.pop();
  undoStack.value.push(bodyData.value.text);
  updateText(nextState, false);
};

const toggleExpandedEditor = () => {
  showExpandedEditor.value = !showExpandedEditor.value;
};

// Tooltip text for undo/redo buttons
const undoTooltip = computed(() => {
  const key = navigator.platform.includes('Mac') ? 'Cmd' : 'Ctrl';
  return `Undo (${key}+Z)`;
});

const redoTooltip = computed(() => {
  const key = navigator.platform.includes('Mac') ? 'Cmd+Shift' : 'Ctrl';
  return `Redo (${key}+Y)`;
});

// Formatting functions
const applyFormatting = async formatType => {
  const textarea = textareaRef.value?.$el?.querySelector('textarea');
  if (!textarea) return;

  const start = textarea.selectionStart;
  const end = textarea.selectionEnd;
  const selectedText = textarea.value.substring(start, end);

  const formatMap = {
    bold: { prefix: '*', suffix: '*' },
    italic: { prefix: '_', suffix: '_' },
    strikethrough: { prefix: '~', suffix: '~' },
    monospace: { prefix: '```', suffix: '```' },
  };

  const format = formatMap[formatType];
  if (!format) return;

  const formattedText = `${format.prefix}${selectedText}${format.suffix}`;

  // Replace selected text
  const newText =
    textarea.value.substring(0, start) +
    formattedText +
    textarea.value.substring(end);

  updateText(newText);

  // Set cursor position after formatting
  await nextTick();
  textarea.focus();
  const newCursorPos = start + format.prefix.length + selectedText.length;
  textarea.setSelectionRange(newCursorPos, newCursorPos);
};

// Insert variable at cursor
const insertVariable = async () => {
  const textarea = textareaRef.value?.$el?.querySelector('textarea');
  if (!textarea) return;

  const start = textarea.selectionStart;
  const text = textarea.value;

  let variable;

  if (props.parameterType === 'named') {
    // Generate named variables: {{var_a}}, {{var_b}}, {{var_c}}...
    const matches = text.match(/\{\{var_([a-z]+)\}\}/gi) || [];
    const letters = matches.map(m => {
      const match = m.match(/var_([a-z]+)/i);
      return match ? match[1].toLowerCase() : '';
    });

    // Find the next letter in sequence
    let nextLetter = 'a';
    if (letters.length > 0) {
      const lastLetter = letters[letters.length - 1];
      const lastCharCode = lastLetter.charCodeAt(lastLetter.length - 1);

      if (lastCharCode < 122) {
        // 'z' is 122
        nextLetter = String.fromCharCode(lastCharCode + 1);
      } else {
        // After 'z', go to 'aa', 'ab', etc.
        nextLetter = lastLetter + 'a';
      }
    }

    variable = `{{var_${nextLetter}}}`;
  } else {
    // Generate positional variables: {{1}}, {{2}}, {{3}}...
    const matches = text.match(/\{\{(\d+)\}\}/g) || [];
    const numbers = matches.map(m => parseInt(m.match(/\d+/)[0], 10));
    const nextNum = numbers.length > 0 ? Math.max(...numbers) + 1 : 1;

    variable = `{{${nextNum}}}`;
  }

  const newText = text.substring(0, start) + variable + text.substring(start);

  updateText(newText);

  await nextTick();
  textarea.focus();
  textarea.setSelectionRange(start + variable.length, start + variable.length);
};

// Emoji picker functions
const toggleEmojiPicker = () => {
  showEmojiPicker.value = !showEmojiPicker.value;
};

const closeEmojiPicker = () => {
  showEmojiPicker.value = false;
};

const insertEmoji = async emoji => {
  const textarea = textareaRef.value?.$el?.querySelector('textarea');
  if (!textarea) return;

  const start = textarea.selectionStart;
  const text = textarea.value;

  const newText = text.substring(0, start) + emoji + text.substring(start);
  updateText(newText);

  closeEmojiPicker();

  await nextTick();
  textarea.focus();
  textarea.setSelectionRange(start + emoji.length, start + emoji.length);
};

// Keyboard shortcuts
const handleKeydown = event => {
  // Check for Cmd+Z (Mac) or Ctrl+Z (Win/Linux)
  if (
    (event.metaKey || event.ctrlKey) &&
    event.key === 'z' &&
    !event.shiftKey
  ) {
    event.preventDefault();
    undo();
  }
  // Check for Cmd+Shift+Z (Mac) or Ctrl+Y (Win/Linux) for redo
  else if (
    ((event.metaKey || event.ctrlKey) && event.key === 'z' && event.shiftKey) ||
    (event.ctrlKey && event.key === 'y')
  ) {
    event.preventDefault();
    redo();
  }
};

onMounted(() => {
  document.addEventListener('keydown', handleKeydown);
});

onUnmounted(() => {
  document.removeEventListener('keydown', handleKeydown);
});
</script>

<template>
  <div class="bg-n-solid-2 rounded-lg p-4 border border-n-weak">
    <div class="flex items-center justify-between mb-4">
      <div class="flex items-center gap-3">
        <span class="i-lucide-align-left size-5 text-n-slate-11" />
        <h4 class="font-medium text-n-slate-12">
          {{ t('SETTINGS.TEMPLATES.BUILDER.BODY.TITLE') }}
        </h4>
        <span class="text-xs text-red-600 bg-red-50 px-2 py-1 rounded">
          {{ t('SETTINGS.TEMPLATES.BUILDER.REQUIRED') }}
        </span>
      </div>
    </div>

    <!-- Content -->
    <div>
      <label class="block text-sm font-medium text-n-slate-12 mb-2">
        {{ t('SETTINGS.TEMPLATES.BUILDER.BODY.TEXT_LABEL') }}
      </label>

      <!-- Formatting Toolbar -->
      <div
        class="flex items-center justify-between gap-1 mb-2 pb-2 border-b border-n-weak overflow-x-auto"
      >
        <div class="flex items-center gap-1 flex-shrink-0">
          <!-- Text Formatting Group -->
          <div class="flex items-center gap-1">
            <NextButton
              v-tooltip.top="t('SETTINGS.TEMPLATES.BUILDER.BODY.TOOLBAR.BOLD')"
              icon="i-lucide-bold"
              slate
              faded
              sm
              @click="applyFormatting('bold')"
            />
            <NextButton
              v-tooltip.top="
                t('SETTINGS.TEMPLATES.BUILDER.BODY.TOOLBAR.ITALIC')
              "
              icon="i-lucide-italic"
              slate
              faded
              sm
              @click="applyFormatting('italic')"
            />
            <NextButton
              v-tooltip.top="
                t('SETTINGS.TEMPLATES.BUILDER.BODY.TOOLBAR.STRIKETHROUGH')
              "
              icon="i-lucide-strikethrough"
              slate
              faded
              sm
              @click="applyFormatting('strikethrough')"
            />
            <NextButton
              v-tooltip.top="
                t('SETTINGS.TEMPLATES.BUILDER.BODY.TOOLBAR.MONOSPACE')
              "
              icon="i-lucide-code"
              slate
              faded
              sm
              @click="applyFormatting('monospace')"
            />
          </div>

          <div class="w-px h-6 bg-n-weak mx-1" />

          <!-- Insert Group -->
          <div class="flex items-center gap-1 relative">
            <div v-on-click-outside="closeEmojiPicker" class="relative">
              <NextButton
                v-tooltip.top="
                  t('SETTINGS.TEMPLATES.BUILDER.BODY.TOOLBAR.EMOJI')
                "
                icon="i-ph-smiley-sticker"
                slate
                faded
                sm
                @click="toggleEmojiPicker"
              />
              <div
                v-if="showEmojiPicker"
                class="absolute top-full mt-1 left-0 z-50 w-80 sm:w-96 h-80 sm:h-96 bg-n-solid-1 rounded-xl shadow-lg border border-n-strong overflow-hidden"
              >
                <EmojiPicker @select="insertEmoji" />
              </div>
            </div>
            <NextButton
              v-tooltip.top="
                t('SETTINGS.TEMPLATES.BUILDER.BODY.TOOLBAR.VARIABLE')
              "
              icon="i-lucide-braces"
              slate
              faded
              sm
              @click="insertVariable"
            />
          </div>

          <div class="w-px h-6 bg-n-weak mx-1" />

          <!-- Undo/Redo Group -->
          <div class="flex items-center gap-1">
            <NextButton
              v-tooltip.top="undoTooltip"
              icon="i-lucide-undo"
              slate
              faded
              sm
              :disabled="undoStack.length === 0"
              @click="undo"
            />
            <NextButton
              v-tooltip.top="redoTooltip"
              icon="i-lucide-redo"
              slate
              faded
              sm
              :disabled="redoStack.length === 0"
              @click="redo"
            />
          </div>
        </div>

        <!-- Expand Button (flex-shrink-0 to prevent it from being hidden) -->
        <NextButton
          v-tooltip.top="t('SETTINGS.TEMPLATES.BUILDER.BODY.EXPAND')"
          icon="i-lucide-maximize-2"
          slate
          faded
          sm
          class="flex-shrink-0 ml-1"
          @click="toggleExpandedEditor"
        />
      </div>

      <TextArea
        ref="textareaRef"
        :model-value="bodyData.text"
        :placeholder="t('SETTINGS.TEMPLATES.BUILDER.BODY.TEXT_PLACEHOLDER')"
        :max-length="1024"
        show-character-count
        @update:model-value="updateText"
      />

      <!-- Formatting Help Text -->
      <p class="text-xs text-n-slate-11 mt-2">
        {{ t('SETTINGS.TEMPLATES.BUILDER.BODY.FORMATTING_HELP') }}
      </p>
    </div>

    <!-- Expanded Editor Modal -->
    <div
      v-if="showExpandedEditor"
      class="fixed inset-0 z-[100] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
      @click="toggleExpandedEditor"
    >
      <div
        class="bg-n-solid-1 rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col"
        @click.stop
      >
        <!-- Modal Header -->
        <div
          class="flex items-center justify-between px-6 py-4 border-b border-n-weak"
        >
          <h3 class="text-lg font-semibold text-n-slate-12">
            {{ t('SETTINGS.TEMPLATES.BUILDER.BODY.TEXT_LABEL') }}
          </h3>
          <button
            class="i-lucide-x size-6 text-n-slate-11 hover:text-n-slate-12"
            @click="toggleExpandedEditor"
          />
        </div>

        <!-- Modal Body -->
        <div class="flex-1 p-6 overflow-auto">
          <TextArea
            :model-value="bodyData.text"
            :placeholder="t('SETTINGS.TEMPLATES.BUILDER.BODY.TEXT_PLACEHOLDER')"
            :max-length="1024"
            show-character-count
            class="min-h-[400px]"
            @update:model-value="updateText"
          />
        </div>

        <!-- Modal Footer -->
        <div
          class="flex items-center justify-end gap-3 px-6 py-4 border-t border-n-weak"
        >
          <NextButton slate faded label="Close" @click="toggleExpandedEditor" />
        </div>
      </div>
    </div>
  </div>
</template>
